import type { ReactNode } from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { cn } from './utils';

/**
 * Page-grammar primitive (SIL-538, W0 of
 * resources/plans/wayfinder-ux-consistency-spec.md §1.1-1.2). Preserves
 * DESIGN.md's amber-fallback convention: "a figure derived from a fallback
 * says so in amber before it says anything else" — this is that callout,
 * generalized so every route reaches for one component instead of hand-
 * rolling the amber-bordered box each time (the Calculation Basis Drawer's
 * own callout is the canonical example this backs). `tone="neutral"` covers
 * the non-fallback "insight" case without inventing a second component.
 */
export type InsightBannerTone = 'amber' | 'neutral';

const TONE_STYLES: Record<
  InsightBannerTone,
  { wrap: string; icon: string; lead: string; Icon: typeof AlertTriangle }
> = {
  amber: {
    wrap: 'border-status-amber/40 bg-status-amber/10',
    icon: 'text-status-amber',
    lead: 'text-status-amber',
    Icon: AlertTriangle,
  },
  neutral: {
    wrap: 'border-border bg-muted',
    icon: 'text-muted-foreground',
    lead: 'text-foreground',
    Icon: Info,
  },
};

export function InsightBanner({
  tone = 'amber',
  lead,
  children,
  action,
  className,
}: {
  tone?: InsightBannerTone;
  /** The strong, plain-language lead line — states the fallback (or
   * insight) before any figure it produced. */
  lead: ReactNode;
  /** Supporting body copy. */
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  const { wrap, icon, lead: leadColor, Icon } = TONE_STYLES[tone];
  return (
    <div className={cn('flex gap-3 rounded-lg border px-4 py-3', wrap, className)}>
      <Icon className={cn('mt-0.5 size-4 shrink-0', icon)} strokeWidth={2.2} />
      <div className="min-w-0 flex-1 text-sm">
        <p className={cn('font-semibold', leadColor)}>{lead}</p>
        {children && <div className="mt-0.5 text-muted-foreground">{children}</div>}
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}
